import React, { useEffect, useState } from "react";
import { Breadcrumb, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import apiFc from "../../../services/APIFc";
import { decryptData } from "../../../functions/cryptoUtils";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { AlertDialogInterface } from "../../common/Interfaces";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { TablaDataBasica } from "../../common/TablaDataBasica";

interface Presupuesto {
  id: number;
  fechaCreacion: string;
  fechaValidez: string;
  descripcion: string;
  clienteDescripcion: string;
  bonificacionGeneral: number;
  fletePrecio: number;
  observacionPie: string;
  observacionInterna: string | null;
  estado: {
    id: number;
    descripcion: string;
    activo: boolean;
  };
}

interface NuevoPresupuesto {
  fechaValidez: string;
  descripcion: string;
  clienteDescripcion: string;
  bonificacionGeneral?: number;
  fletePrecio?: number;
  observacionPie?: string;
  observacionInterna?: string;
  clienteCodigo: number;
}

const Presupuestador = () => {
  const encryptedUser = useSelector((store: any) => store.authreducer.user);
  const user = decryptData(encryptedUser);
  const [presupuestos, setPresupuestos] = useState<Presupuesto[]>([]);
  const [totalPresupuestos, setTotalPresupuestos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [alertDialog, setAlertDialog] = useState<AlertDialogInterface>({
    id: "1",
    visible: false,
    severity: "success",
    timeOut: 2000,
    cabecera: "Éxito!",
    texto: "Operación realizada con éxito.",
  });
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Columnas para la tabla
  const columns = React.useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        className: "text-center",
        rowClassName: "text-center",
      },
      {
        Header: "Fecha Creación",
        accessor: "fechaCreacion",
        className: "text-center",
        rowClassName: "text-center",
        Cell: ({ value }: any) => formatDate(value),
      },
      {
        Header: "Fecha Validez",
        accessor: "fechaValidez",
        className: "text-center",
        rowClassName: "text-center",
        Cell: ({ value }: any) => formatDate(value),
      },
      {
        Header: "Descripción",
        accessor: "descripcion",
        className: "text-center",
        rowClassName: "text-center",
      },
      {
        Header: "Cliente",
        accessor: "clienteDescripcion",
        className: "text-center",
        rowClassName: "text-center",
      },
      {
        // Header: "Estado",
        // accessor: "estado.descripcion",
        // className: "text-center d-flex justify-content-center align-items-center",
        // rowClassName: "text-center",
        // variant: "primary",
        // Cell: ({ row }: any) => (
        //   <span
        //     className={`badge ${
        //       row.original.estado.id === 1
        //         ? "bg-primary"
        //         : row.original.estado.id === 2
        //         ? "bg-success"
        //         : "bg-secondary"
        //     } wd-80p ht-25 sm tx-12 `}
        //   >
        //     {row.original.estado.descripcion}
        //   </span>
        // ),
        Header: "Estado",
        className: "text-center",
        rowClassName: "text-center",
        Cell: ({ row }: any) => (
          <>
            <Button
              variant="primary"
              size="sm"
              className="me-2"
              // onClick={() => handleVerPresupuesto(row.original.id)}
            >
              {row.original.estado.descripcion}
            </Button>
          </>
        ),
      },
      {
        Header: "Acciones",
        className: "text-center",
        rowClassName: "text-center",
        Cell: ({ row }: any) => (
          <>
            <Button
              variant="primary"
              size="sm"
              className="me-2"
              onClick={() => handleVerPresupuesto(row.original.id)}
            >
              Editar
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => generarImpresionPresupuesto(row.original.id)}
            >
              PDF
            </Button>
          </>
        ),
      },
    ],
    []
  );

  // Función de carga modificada
  const cargarPresupuestos = async () => {
    setLoading(true);
    try {
      const response = await apiFc.get("/web/presupuestos/list", {
        params: {
          page: pagination.pageIndex,
          rows: pagination.pageSize,
          filter: filter, // siempre enviar el filter, aunque esté vacío
        },
      });
      setPresupuestos(response.data.content);
      setTotalPresupuestos(response.data.totalElements);
    } catch (error) {
      handleError("Error al cargar presupuestos");
    } finally {
      setLoading(false);
    }
  };

  // Efecto que se ejecuta cuando cambia paginación o filter
  useEffect(() => {
    cargarPresupuestos();
  }, [pagination, filter]); // Se ejecuta cuando cambia paginación O filter

  const handleVerPresupuesto = (id: any) => {
    navigate(`/principal/presupuestadoredicion?id=${id}`);
  };

  const crearNuevoPresupuesto = () => {
    const nuevoPresupuesto: NuevoPresupuesto = {
      fechaValidez: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      descripcion: "Nuevo Presupuesto",
      clienteDescripcion: "Cliente por defecto",
      bonificacionGeneral: 0,
      fletePrecio: 0,
      observacionPie: "",
      observacionInterna: "",
      clienteCodigo: user?.clienteCodigo || 1,
    };

    setLoading(true);
    apiFc
      .post("/web/presupuestos/create", nuevoPresupuesto)
      .then((response) => {
        // Recargar los presupuestos cuando se crea uno nuevo
        cargarPresupuestos();
        // Navegar al editor del presupuesto recien creado
        navigate(`/principal/presupuestadoredicion?id=${response.data.id}`);
        setAlertDialog({
          ...alertDialog,
          visible: true,
          cabecera: "Éxito",
          texto: "Presupuesto creado correctamente",
          severity: "success",
        });
      })
      .catch((error) => {
        const errorMessage =
          error.response?.data?.message || "Error al crear el presupuesto";
        handleError(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleError = (message: string) => {
    setAlertDialog({
      ...alertDialog,
      visible: true,
      cabecera: "Error",
      texto: message,
      severity: "error",
    });
  };

  // Manejar cambio de página y tamaño de página
  const handlePaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  // Manejar búsqueda
  const handleSearch = (searchText: string) => {
    setFilter(searchText);
    // Resetear a la primera página cuando se realiza una nueva búsqueda
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-AR");
  };

  const Mensaje = () => {
    const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
      props,
      ref
    ) {
      return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    const handleClose = (
      event?: React.SyntheticEvent | Event,
      reason?: string
    ) => {
      if (reason === "clickaway") {
        return;
      }
      setAlertDialog({ ...alertDialog, visible: false });
    };

    return (
      <Snackbar
        open={alertDialog.visible}
        autoHideDuration={alertDialog.timeOut}
        onClose={handleClose}
        anchorOrigin={
          isMobile
            ? { vertical: "top", horizontal: "center" }
            : { vertical: "bottom", horizontal: "right" }
        }
      >
        <Alert
          onClose={handleClose}
          // severity={alertDialog.severity}
          sx={{ width: "100%" }}
        >
          <strong>{alertDialog.cabecera} </strong> {alertDialog.texto}
        </Alert>
      </Snackbar>
    );
  };

  const generarImpresionPresupuesto = (id: any) => {
    setLoading(true);
    apiFc
      .get(`/web/presupuestos/report/${id}`, {
        responseType: "blob",
      })
      .then((response) => {
        const fileURL = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = fileURL;
        link.setAttribute("download", `presupuesto_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(fileURL);

        setAlertDialog({
          ...alertDialog,
          visible: true,
          cabecera: "Éxito",
          texto: "PDF generado correctamente",
          severity: "success",
        });
      })
      .catch((error) => {
        handleError("Error al generar el PDF");
      })
      .finally(() => {
        setLoading(false);
      });
  };
    const handleFileUpload = async () => {
      if (!selectedFile) return;
    
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
    
      await apiFc.post(`/web/presupuestos/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        setAlertDialog({
          ...alertDialog,
          visible: true,
          cabecera: "Éxito",
          texto: "Imagen subida correctamente",
          severity: "success",
        });
        setShowUploadModal(false);
        setSelectedFile(null);
      })
      .catch((err: any) => {
        setAlertDialog({
          ...alertDialog,
          visible: true,
          cabecera: "Error",
          texto: `Error al subir imagen: ${err.response?.data?.message || err.message}`,
          severity: "error",
        });
      })
      .finally(() => {
        setUploading(false);
      });
    };

  // const clientesPermitidos = [2268, 1176, 186, 1144];
  // const clienteTienePermiso = clientesPermitidos.includes(user?.clienteCodigo);

  // if (!clienteTienePermiso) {
  //   return (
  //     <div className="container mt-5">
  //       <div className="row justify-content-center">
  //         <div className="col-md-8">
  //           <Card className="text-center border-0 shadow-lg card-hover"
  //                 style={{
  //                   borderRadius: '15px',
  //                   overflow: 'hidden',
  //                   border: 'none'
  //                 }}>
  //             <Card.Header
  //               as="h4"
  //               className="py-3 text-white"
  //               style={{
  //                 background: 'linear-gradient(135deg, rgb(205, 8, 19) 0%, rgb(148, 5, 12) 100%)',
  //                 borderBottom: 'none'
  //               }}>
  //               <i className="fas fa-rocket me-2"></i>
  //               Nuevo Módulo Presupuestador
  //             </Card.Header>
  //             <Card.Body className="py-4 px-5" style={{backgroundColor: '#f8f9fa'}}>
  //               <div className="mb-4">
  //                 <div className="icon-container mb-3">
  //                   <i className="fas fa-lightbulb fa-3x"
  //                      style={{color: 'rgb(205, 8, 19)'}}></i>
  //                 </div>
  //                 <Card.Title className="h4 mb-3" style={{color: 'rgb(205, 8, 19)'}}>
  //                   Potencia tu negocio
  //                 </Card.Title>
  //                 <Card.Text className="text-muted mb-4">
  //                   <strong>¡Estamos desarrollando una nueva herramienta para ti!</strong>
  //                   <br /><br />
  //                   Próximamente podrás crear presupuestos completos para tus clientes,
  //                   combinando artículos de ferretería con productos de otros proveedores
  //                   en un mismo documento profesional.
  //                   <br /><br />
  //                   Simplificaremos todo el proceso para que ahorres tiempo y puedas
  //                   enfocarte en hacer crecer tu negocio.
  //                 </Card.Text>
  //               </div>
  //               <div className="d-flex justify-content-center gap-3">
  //                 <Button
  //                   variant="outline-danger"
  //                   onClick={() => navigate("/principal/inicio")}
  //                   className="px-4 py-2 rounded-pill"
  //                   style={{
  //                     fontWeight: '600',
  //                     borderWidth: '2px',
  //                     transition: 'all 0.3s ease'
  //                   }}>
  //                   <i className="fas fa-home me-2"></i>
  //                   Volver al inicio
  //                 </Button>
  //               </div>
  //             </Card.Body>
  //             <Card.Footer className="py-3"
  //                          style={{
  //                            backgroundColor: 'rgba(205, 8, 19, 0.05)',
  //                            borderTop: '1px solid rgba(205, 8, 19, 0.1)'
  //                          }}>
  //               <small className="text-muted">
  //                 <i className="fas fa-clock me-1"></i>
  //                 Disponible próximamente - Solicítalo ahora para tener acceso prioritario
  //               </small>
  //             </Card.Footer>
  //           </Card>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div>
      <div className="breadcrumb-header justify-content-between">
        <div className="left-content">
          <span className="main-content-title mg-b-0 mg-b-lg-1">
            Presupuestos
          </span>
          {alertDialog.visible && <Mensaje />}
        </div>
         <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>Subir Imagen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form.Group>
                    <Form.Label>Aqui podras cargar el logo de tu negocio y asi verlo en la cabecera de todos los presupuestos que crees</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".png"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.files && e.target.files[0]) {
                          setSelectedFile(e.target.files[0]);
                        }
                      }}
                    />
                  </Form.Group>
                  {selectedFile && (
                    <div className="mt-3">
                      <p>Archivo seleccionado: {selectedFile.name}</p>
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Preview"
                        style={{ maxWidth: "100%", maxHeight: "200px" }}
                      />
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleFileUpload}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? "Subiendo..." : "Subir Imagen"}
                  </Button>
                </Modal.Footer>
              </Modal>
        <div className="justify-content-center mt-2">
          <Breadcrumb className="breadcrumb">
            <Breadcrumb.Item
              className="breadcrumb-item btn-nuevo"
              active
              aria-current="page"
              onClick={() => setShowUploadModal(true)}
            >
              <Button>
             Personaliza tu presupuesto
              </Button>
            </Breadcrumb.Item>
            <Breadcrumb.Item
              className="breadcrumb-item btn-nuevo"
              active
              aria-current="page"
              onClick={crearNuevoPresupuesto}
            >
              <Button>
                <i className="fa fa-plus me-2"></i>Nuevo Presupuesto
              </Button>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
      </div>
      <div className="panel panel-primary tabs-style-2">
        <div className="tab-menu-heading">
          <div className="tabs-menu1">
          <Card>
              <Card.Body>
                {loading ? (
                  <div className="text-center my-5 py-5">
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                    <div className="mt-2">Cargando presupuestos...</div>
                  </div>
                ) : (
                  <>
                    <TablaDataBasica
                      columns={columns}
                      data={presupuestos}
                      loading={loading}
                      mostrarFiltro={true}
                      mostrarCantidadRgistros={true}
                      onPageChange={(pageIndex: any, pageSize: any) =>
                        setPagination({ pageIndex, pageSize })
                      }
                      pageCount={Math.ceil(totalPresupuestos / pagination.pageSize)}
                      initialPageSize={pagination.pageSize}
                      onSearch={handleSearch}
                    />
                    
                    {presupuestos.length === 0 && (
                      <div className="text-center py-5">
                        <h5>No hay presupuestos creados</h5>
                        <Button
                          variant="primary"
                          className="mt-3"
                          onClick={crearNuevoPresupuesto}
                        >
                          Crear primer presupuesto
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Presupuestador;
